import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  BookOpen,
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock3,
  LogOut,
  Mail,
  Save,
  User,
} from "lucide-react";
import {
  extractTeacherRecord,
  getTeacherEmail,
  teacherApi,
} from "../../features/teacher/teacherApi";
import {
  clearTeacherSession,
  updateTeacherProfile,
} from "../../features/teacher/teacherSlice";

const availabilityStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Lato:wght@300;400;700;900&display=swap');

  .teacher-availability-page {
    min-height: 100vh;
    background: #faf5ec;
    color: #1f1a17;
    font-family: "Lato", sans-serif;
    font-size: 1rem;
    position: relative;
  }

  .teacher-availability-popup {
    position: fixed;
    top: 20%;
    right: 2rem;
    z-index: 10000;
    animation: slideInRight 0.3s ease-out;
    max-width: 400px;
    width: 90vw;
    max-width: min(90vw, 400px);
  }

  .teacher-availability-popup-success {
    background: linear-gradient(135deg, #effcf2 0%, #d4f8db 100%);
    border: 2px solid #b9efca;
    box-shadow: 0 20px 40px rgba(27, 139, 88, 0.25);
  }

  .teacher-availability-popup-error {
    background: linear-gradient(135deg, #fff3f0 0%, #ffd1c7 100%);
    border: 2px solid #ffd1c7;
    box-shadow: 0 20px 40px rgba(179, 71, 54, 0.25);
  }

  .teacher-availability-popup-content {
    padding: 1.25rem 1.5rem;
    border-radius: 12px;
    position: relative;
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .teacher-availability-popup-message {
    flex: 1;
    font-size: 0.95rem;
    font-weight: 500;
    line-height: 1.5;
    color: #1f1a17;
  }

  .teacher-availability-popup-close {
    position: absolute;
    top: 0.5rem;
    right: 0.75rem;
    background: none;
    border: none;
    font-size: 1.5rem;
    font-weight: bold;
    color: #6f6a67;
    cursor: pointer;
    width: 2rem;
    height: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: all 0.2s ease;
  }

  .teacher-availability-popup-close:hover {
    background: rgba(0,0,0,0.1);
    color: #1f1a17;
  }

  @keyframes slideInRight {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    .teacher-availability-popup {
      right: 1rem;
      left: 1rem;
      top: 15%;
      width: calc(100% - 2rem);
    }
  }

  .teacher-availability-topbar {
    position: sticky;
    top: 0;
    z-index: 10;
    background: rgba(255, 255, 255, 0.95);
    border-bottom: 1px solid #ebe4d8;
    backdrop-filter: blur(14px);
  }

  .teacher-availability-shell {
    width: min(100%, 78rem);
    margin: 0 auto;
    padding-inline: clamp(0.875rem, 2vw, 1.25rem);
  }

  .teacher-availability-topbar-row {
    min-height: clamp(4rem, 5.5vw, 4.5rem);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: clamp(1rem, 2vw, 1.5rem);
  }

  .teacher-availability-brand {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .teacher-availability-brand-badge {
    width: clamp(2.25rem, 2.8vw, 2.45rem);
    height: clamp(2.25rem, 2.8vw, 2.45rem);
    border-radius: 999px;
    display: grid;
    place-items: center;
    background: linear-gradient(135deg, #ff7154 0%, #ff564d 100%);
    color: #ffffff;
    box-shadow: 0 12px 20px rgba(255, 98, 77, 0.2);
  }

  .teacher-availability-brand-title {
    margin: 0;
    color: #ff624d;
    font-size: clamp(1.1rem, 1.7vw, 1.35rem);
    font-weight: 700;
  }

  .teacher-availability-actions {
    display: flex;
    align-items: center;
    gap: clamp(0.75rem, 1.5vw, 1rem);
  }

  .teacher-availability-email-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.625rem;
    min-height: 2.5rem;
    padding-inline: 1rem;
    border: 1px solid #dbe3f2;
    border-radius: 999px;
    background: #ffffff;
    color: #6f6a67;
    font-size: 0.9rem;
    white-space: nowrap;
  }

  .teacher-availability-logout {
    display: inline-flex;
    align-items: center;
    gap: 0.625rem;
    border: 0;
    background: transparent;
    color: #1f1a17;
    font: inherit;
    font-size: 0.94rem;
    cursor: pointer;
  }

  .teacher-availability-tabs {
    display: flex;
    align-items: center;
    gap: clamp(0.625rem, 1.5vw, 0.95rem);
    min-height: 3.35rem;
  }

  .teacher-availability-tab {
    min-height: 3.35rem;
    display: inline-flex;
    align-items: center;
    gap: 0.625rem;
    padding-inline: 0.95rem;
    border: 0;
    border-bottom: 0.1875rem solid transparent;
    background: transparent;
    color: #2f2a28;
    font: inherit;
    font-size: 0.95rem;
    cursor: pointer;
  }

  .teacher-availability-tab-active {
    color: #2453d4;
    border-bottom-color: #2453d4;
    background: rgba(226, 235, 255, 0.78);
    border-top-left-radius: 0.875rem;
    border-top-right-radius: 0.875rem;
  }

  .teacher-availability-content {
    padding: clamp(1.25rem, 2.5vw, 1.65rem) clamp(0.875rem, 1.6vw, 1.25rem)
      clamp(2rem, 4vw, 2.75rem);
  }

  .teacher-availability-banner {
    border: 1px solid #d7e3fb;
    border-radius: 1rem;
    background: #dfe8fb;
    padding: clamp(1.25rem, 2vw, 1.6rem);
    margin-bottom: 1.5rem;
  }

  .teacher-availability-banner h2 {
    margin: 0 0 0.75rem;
    font-size: clamp(0.95rem, 1.3vw, 1.04rem);
    font-weight: 500;
  }

  .teacher-availability-banner p {
    margin: 0;
    color: #75706c;
    font-size: 0.9rem;
  }

  .teacher-availability-status-stack {
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
    margin-bottom: 1rem;
  }

  .teacher-availability-message {
    border-radius: 0.9rem;
    border: 1px solid transparent;
    padding: 0.95rem 1rem;
    font-size: 0.92rem;
  }

  .teacher-availability-message-success {
    border-color: #b9efca;
    background: #effcf2;
    color: #1b8b58;
  }

  .teacher-availability-message-error {
    border-color: #ffd1c7;
    background: #fff3f0;
    color: #b34736;
  }

  .teacher-availability-message-info {
    border-color: #d7e3fb;
    background: #eef4ff;
    color: #315db7;
  }

  .teacher-availability-selected-date {
    margin-bottom: 1.75rem;
  }

  .teacher-availability-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(18rem, 0.98fr);
    gap: 1.5rem;
  }

  .teacher-availability-card {
    border: 1px solid #dde5ef;
    border-radius: 1rem;
    background: #ffffff;
    padding: clamp(1rem, 1.8vw, 1.3rem);
  }

  .teacher-availability-calendar-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  .teacher-availability-calendar-title {
    margin: 0;
    color: #ff624d;
    font-size: clamp(0.95rem, 1.3vw, 1.04rem);
    font-weight: 500;
  }

  .teacher-availability-calendar-controls {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .teacher-availability-control {
    width: 2.2rem;
    height: 2.2rem;
    display: grid;
    place-items: center;
    border: 0;
    border-radius: 999px;
    background: transparent;
    color: #9a9a9a;
    cursor: pointer;
  }

  .teacher-availability-weekdays,
  .teacher-availability-days {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    text-align: center;
  }

  .teacher-availability-weekdays {
    margin-bottom: 0.75rem;
    color: #3a3836;
    font-size: 0.95rem;
    font-weight: 500;
  }

  .teacher-availability-days {
    gap: 0.35rem 0;
  }

  .teacher-availability-day {
    min-height: 5.5rem;
    display: grid;
    place-items: center;
  }

  .teacher-availability-day-empty {
    pointer-events: none;
  }

  .teacher-availability-day-button {
    width: 3.4rem;
    height: 3.4rem;
    display: grid;
    place-items: center;
    border: 0;
    border-radius: 999px;
    background: transparent;
    color: #1f1a17;
    font: inherit;
    font-size: 1rem;
    cursor: pointer;
    transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
  }

  .teacher-availability-day-button:hover {
    background: #f5efe5;
  }

  .teacher-availability-day-selected .teacher-availability-day-button {
    width: 3.4rem;
    height: 3.4rem;
    background: #ff624d;
    box-shadow: 0 14px 24px rgba(255, 98, 77, 0.24);
    color: #ffffff;
  }

  .teacher-availability-slots-head {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  .teacher-availability-slots-icon {
    width: 3.4rem;
    height: 3.4rem;
    border-radius: 999px;
    display: grid;
    place-items: center;
    background: #000000;
    color: #ffffff;
    box-shadow: 0 14px 24px rgba(0, 0, 0, 0.12);
  }

  .teacher-availability-slots-head h2 {
    margin: 0;
    color: #ff624d;
    font-size: clamp(0.95rem, 1.3vw, 1.04rem);
    font-weight: 500;
  }

  .teacher-availability-slot-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    max-height: calc((4rem * 6) + (0.75rem * 5));
    overflow-y: auto;
    padding-right: 0.35rem;
  }

  .teacher-availability-slot-list::-webkit-scrollbar {
    width: 0.45rem;
  }

  .teacher-availability-slot-list::-webkit-scrollbar-thumb {
    background: #d2dbef;
    border-radius: 999px;
  }

  .teacher-availability-slot-list::-webkit-scrollbar-track {
    background: transparent;
  }

  .teacher-availability-slot {
    width: 100%;
    min-height: 4rem;
    padding: 0.75rem 1.25rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    border: 1px solid #dde5ef;
    border-radius: 0.85rem;
    background: #ffffff;
    color: #1f1a17;
    font: inherit;
    cursor: pointer;
    transition: border-color 0.2s ease, background 0.2s ease,
      box-shadow 0.2s ease, color 0.2s ease;
  }

  .teacher-availability-slot:hover {
    border-color: #c9d8fb;
  }

  .teacher-availability-slot-active {
    border-color: #2d61f0;
    background: #f5f8ff;
    box-shadow: 0 10px 22px rgba(45, 97, 240, 0.12);
    color: #2348a8;
  }

  .teacher-availability-slot-label {
    font-size: 0.98rem;
    font-weight: 500;
  }

  .teacher-availability-slot-badge {
    min-height: 2.2rem;
    padding-inline: 0.95rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.45rem;
    border-radius: 999px;
    background: #2d61f0;
    color: #ffffff;
    font-size: 0.95rem;
    font-weight: 500;
    white-space: nowrap;
  }

  .teacher-availability-save {
    width: 100%;
    min-height: 3.4rem;
    margin-top: 1.25rem;
    border: 0;
    border-radius: 0.85rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    background: linear-gradient(135deg, #ff7154 0%, #ff564d 100%);
    box-shadow: 0 16px 26px rgba(255, 98, 77, 0.2);
    color: #ffffff;
    font: inherit;
    font-size: 0.98rem;
    font-weight: 700;
    cursor: pointer;
  }

  .teacher-availability-save:disabled {
    cursor: not-allowed;
    opacity: 0.75;
  }

  @media (max-width: 980px) {
    .teacher-availability-grid {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 768px) {
    .teacher-availability-topbar-row {
      min-height: auto;
      padding: 1.125rem 0;
      flex-direction: column;
      align-items: flex-start;
    }

    .teacher-availability-actions {
      width: 100%;
      flex-direction: column;
      align-items: stretch;
    }

    .teacher-availability-email-pill,
    .teacher-availability-logout {
      justify-content: center;
    }

    .teacher-availability-tabs {
      overflow-x: auto;
      padding-bottom: 0.5rem;
    }

    .teacher-availability-tab {
      white-space: nowrap;
    }
  }
`;

const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const timeSlots = Array.from({ length: 12 }, (_, index) => {
  const startHour = index + 9;
  const endHour = startHour + 1;

  return `${String(startHour).padStart(2, "0")}:00 - ${String(endHour).padStart(
    2,
    "0"
  )}:00`;
});

const formatDateKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;

const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();

const getCalendarCells = (monthDate) => {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstWeekday = new Date(year, month, 1).getDay();
  const totalDays = getDaysInMonth(year, month);

  return [
    ...Array.from({ length: firstWeekday }, (_, index) => ({
      type: "blank",
      id: `blank-${index}`,
    })),
    ...Array.from({ length: totalDays }, (_, index) => ({
      type: "day",
      value: index + 1,
    })),
  ];
};

const buildAvailabilityPayload = (dateKey, slot) => {
  const [startTime = "", endTime = ""] = String(slot).split(" - ");

  return {
    date: dateKey,
    start_time: startTime.trim(),
    end_time: endTime.trim(),
    slot_duration: 60,
  };
};

export default function TeacherAvailability() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const teacherSession = useSelector((state) => state.teacher?.teacher);
  const today = useMemo(() => new Date(), []);
  const [currentMonth, setCurrentMonth] = useState(
    new Date(today.getFullYear(), today.getMonth(), 1)
  );
  const [selectedDate, setSelectedDate] = useState(today);
  const [availabilityByDate, setAvailabilityByDate] = useState({});
const [popup, setPopup] = useState({ show: false, type: '', message: '' });

  const profileQuery = useQuery({
    queryKey: ["teacher-availability-profile"],
    queryFn: teacherApi.getProfile,
    retry: false,
  });

  useEffect(() => {
    const teacherRecord = extractTeacherRecord(profileQuery.data);
    if (teacherRecord) {
      dispatch(updateTeacherProfile(teacherRecord));
    }
  }, [dispatch, profileQuery.data]);

  const teacherProfile =
    extractTeacherRecord(profileQuery.data, teacherSession || {}) || teacherSession || {};
  const teacherEmail = getTeacherEmail(teacherProfile);

  const selectedDateKey = formatDateKey(selectedDate);
  const selectedSlots = availabilityByDate[selectedDateKey] || [];
  const calendarCells = getCalendarCells(currentMonth);
  const currentMonthLabel = currentMonth.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  const saveAvailabilityMutation = useMutation({
    mutationFn: async ({ dateKey, slots }) => {
      const successes = [];
      const failures = [];

      for (const slot of slots) {
        try {
          const response = await teacherApi.createAvailability(
            buildAvailabilityPayload(dateKey, slot)
          );
          successes.push({ slot, response });
        } catch (error) {
          failures.push({ slot, error });
        }
      }

      return { successes, failures };
    },
    onSuccess: ({ successes, failures }) => {
      if (successes.length) {
        // Use backend message from first success if available
        const backendMessage = successes[0]?.response?.data?.message || 
          `Availability created and slots generated successfully for ${successes.length} slot(s).`;
        showPopup('success', backendMessage);
      }

      if (failures.length) {
        const firstFailure = failures[0]?.error;
        const backendMessage = firstFailure?.response?.data?.message || firstFailure?.message ||
          "One or more slots could not be saved. Please review and retry.";
        showPopup('error', backendMessage);
      }
    },
    onError: (error) => {
      const backendMessage = error?.response?.data?.message || error?.message || "Failed to save availability.";
      showPopup('error', backendMessage);
    },
  });

  const showPopup = (type, message) => {
    setPopup({ show: true, type, message });
    setTimeout(() => setPopup({ show: false, type: '', message: '' }), 4000);
  };

  const handleLogout = () => {
    dispatch(clearTeacherSession());
    navigate("/teacherlogin", { replace: true });
  };

  const changeMonth = (offset) => {
    const nextMonth = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + offset,
      1
    );
    const nextDay = Math.min(
      selectedDate.getDate(),
      getDaysInMonth(nextMonth.getFullYear(), nextMonth.getMonth())
    );

    setCurrentMonth(nextMonth);
    setSelectedDate(
      new Date(nextMonth.getFullYear(), nextMonth.getMonth(), nextDay)
    );
  };

  const handleDateSelect = (day) => {
    setSelectedDate(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day)
    );
  };

  const toggleSlot = (slot) => {
    setAvailabilityByDate((previous) => {
      const existingSlots = previous[selectedDateKey] || [];
      const nextSlots = existingSlots.includes(slot)
        ? existingSlots.filter((item) => item !== slot)
        : [...existingSlots, slot];

      return {
        ...previous,
        [selectedDateKey]: nextSlots,
      };
    });
  };

  const handleSave = () => {
    if (!selectedSlots.length) {
      showPopup('error', "Select at least one time slot before saving.");
      return;
    }

    saveAvailabilityMutation.mutate({
      dateKey: selectedDateKey,
      slots: selectedSlots,
    });
  };

  return (
    <>
      <style>{availabilityStyles}</style>
      <main className="teacher-availability-page">
        <header className="teacher-availability-topbar">
          <div className="teacher-availability-shell">
            <div className="teacher-availability-topbar-row">
              <div className="teacher-availability-brand">
                <div
                  className="teacher-availability-brand-badge"
                  aria-hidden="true"
                >
                  <BookOpen size={20} />
                </div>
                <h1 className="teacher-availability-brand-title">
                  Teacher Dashboard
                </h1>
              </div>

              <div className="teacher-availability-actions">
                <div className="teacher-availability-email-pill">
                  <Mail size={18} />
                  {teacherEmail || "Teacher account"}
                </div>
                <button
                  className="teacher-availability-logout"
                  type="button"
                  onClick={handleLogout}
                >
                  <LogOut size={18} />
                  Logout
                </button>
              </div>
            </div>
          </div>
        </header>

        <div className="teacher-availability-shell">
          <nav className="teacher-availability-tabs" aria-label="Teacher tabs">
            <button
              className="teacher-availability-tab"
              type="button"
              onClick={() => navigate("/teacherdashboard")}
            >
              <BookOpen size={20} />
              Bookings
            </button>
            <button
              className="teacher-availability-tab teacher-availability-tab-active"
              type="button"
            >
              <CalendarDays size={20} />
              Availability
            </button>
            <button
              className="teacher-availability-tab"
              type="button"
              onClick={() => navigate("/teacherprofile")}
            >
              <User size={20} />
              Profile
            </button>
          </nav>
        </div>

        <div className="teacher-availability-shell teacher-availability-content">
          <section className="teacher-availability-banner">
            <h2>Manage Your Availability</h2>
            <p>
              Select the time slots when you&apos;re available to teach. Each
              selected tile saves one 60-minute slot to the backend.
            </p>
          </section>

          {profileQuery.isError ? (
            <div className="teacher-availability-message teacher-availability-message-error" style={{ marginBottom: '1rem' }}>
              {profileQuery.error.message}
            </div>
          ) : null}
          <div className="teacher-availability-message teacher-availability-message-info teacher-availability-selected-date">
            Selected date: {selectedDateKey}
          </div>

          {popup.show && (
            <div className={`teacher-availability-popup teacher-availability-popup-${popup.type}`}>
              <div className="teacher-availability-popup-content">
                <span className="teacher-availability-popup-message">{popup.message}</span>
                <button 
                  className="teacher-availability-popup-close" 
                  onClick={() => setPopup({ show: false, type: '', message: '' })}
                  aria-label="Close popup"
                >
                  ×
                </button>
              </div>
            </div>
          )}

          <section className="teacher-availability-grid">
            <article className="teacher-availability-card">
              <div className="teacher-availability-calendar-header">
                <h2 className="teacher-availability-calendar-title">{currentMonthLabel}</h2>
                <div className="teacher-availability-calendar-controls">
                  <button
                    className="teacher-availability-control"
                    type="button"
                    onClick={() => changeMonth(-1)}
                    aria-label="Previous month"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    className="teacher-availability-control"
                    type="button"
                    onClick={() => changeMonth(1)}
                    aria-label="Next month"
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>

              <div className="teacher-availability-weekdays">
                {weekdays.map((day) => (
                  <span key={day}>{day}</span>
                ))}
              </div>

              <div className="teacher-availability-days">
                {calendarCells.map((day, index) => {
                  if (day.type === "blank") {
                    return (
                      <div
                        key={day.id || `blank-${index}`}
                        className="teacher-availability-day teacher-availability-day-empty"
                      />
                    );
                  }

                  const isSelected =
                    selectedDate.getFullYear() === currentMonth.getFullYear() &&
                    selectedDate.getMonth() === currentMonth.getMonth() &&
                    selectedDate.getDate() === day.value;

                  return (
                    <div
                      key={day.value}
                      className={`teacher-availability-day${
                        isSelected ? " teacher-availability-day-selected" : ""
                      }`}
                    >
                      <button
                        className="teacher-availability-day-button"
                        type="button"
                        onClick={() => handleDateSelect(day.value)}
                        aria-pressed={isSelected}
                        aria-label={`Select ${currentMonthLabel} ${day.value}`}
                      >
                        {day.value}
                      </button>
                    </div>
                  );
                })}
              </div>
            </article>

            <article className="teacher-availability-card">
              <div className="teacher-availability-slots-head">
                <div className="teacher-availability-slots-icon" aria-hidden="true">
                  <Clock3 size={22} />
                </div>
                <h2>Available Time Slots</h2>
              </div>

              <div className="teacher-availability-slot-list">
                {timeSlots.map((slot) => {
                  const isSelected = selectedSlots.includes(slot);

                  return (
                    <button
                      key={slot}
                      className={`teacher-availability-slot${
                        isSelected ? " teacher-availability-slot-active" : ""
                      }`}
                      type="button"
                      onClick={() => toggleSlot(slot)}
                      aria-pressed={isSelected}
                    >
                      <span className="teacher-availability-slot-label">{slot}</span>
                      {isSelected ? (
                        <span className="teacher-availability-slot-badge">
                          <Check size={16} />
                          Available
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>

              <button
                className="teacher-availability-save"
                type="button"
                onClick={handleSave}
                disabled={saveAvailabilityMutation.isPending}
              >
                <Save size={18} />
                {saveAvailabilityMutation.isPending ? "Saving..." : "Save Availability"}
              </button>
            </article>
          </section>
        </div>
      </main>
    </>
  );
}
